import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import { shareContent } from "../utils/share";
import {
  Trophy, ChevronRight, ChevronLeft, Play, Pause,
  Maximize2, Minimize2, ArrowLeft, Share2, Star,
} from "lucide-react";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api")
  .replace(/\/api\/?$/, "");

function imgUrl(path) {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API_BASE}/${path.replace(/^\//, "")}`;
}

// ── Alturas de podio dinámicas ───────────────────────────────────────────────
function computePodiumHeights() {
  const h = typeof window !== "undefined" ? window.innerHeight : 800;
  const base = Math.min(260, Math.max(80, h * 0.20));
  return { 1: Math.round(base), 2: Math.round(base * 0.67), 3: Math.round(base * 0.46) };
}

function usePodiumHeights() {
  const [heights, setHeights] = useState(computePodiumHeights);
  useEffect(() => {
    const onResize = () => setHeights(computePodiumHeights());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return heights;
}

// ── Confetti ────────────────────────────────────────────────────────────────
const CONFETTI_COLORS = ["#FFD700","#FF6B35","#ffffff","#FF4081","#40C4FF","#69F0AE","#E040FB"];

function Confetti({ active, count = 80 }) {
  const particles = useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i, x: Math.random() * 100,
      size: Math.random() * 14 + 6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 0.8,
      duration: Math.random() * 2 + 2,
      rotate: Math.random() * 720,
      type: i % 3,
    })), [count]);

  if (!active) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map(p => (
        <motion.div key={p.id}
          initial={{ y: -30, x: `${p.x}vw`, opacity: 1, rotate: 0 }}
          animate={{ y: "115vh", opacity: [1, 1, 0.5, 0], rotate: p.rotate }}
          transition={{ duration: p.duration, delay: p.delay, ease: "easeIn" }}
          style={{
            position: "absolute",
            width: p.size, height: p.type === 2 ? 0 : p.size,
            backgroundColor: p.type !== 2 ? p.color : "transparent",
            borderRadius: p.type === 0 ? "50%" : "2px",
            borderLeft:   p.type === 2 ? `${p.size/2}px solid transparent` : undefined,
            borderRight:  p.type === 2 ? `${p.size/2}px solid transparent` : undefined,
            borderBottom: p.type === 2 ? `${p.size}px solid ${p.color}` : undefined,
          }}
        />
      ))}
    </div>
  );
}

// ── Partículas flotantes ────────────────────────────────────────────────────
function FloatingParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i, x: Math.random() * 100, y: Math.random() * 100,
      size: Math.random() * 4 + 1, delay: Math.random() * 5,
      gold: Math.random() > 0.5,
    })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <motion.div key={p.id} className="absolute rounded-full"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, backgroundColor: p.gold ? "#FFD700" : "white" }}
          animate={{ opacity: [0.1, p.gold ? 0.9 : 0.5, 0.1], scale: [1, 1.6, 1], y: [0, -22, 0] }}
          transition={{ duration: 3 + Math.random() * 2.5, delay: p.delay, repeat: Infinity }}
        />
      ))}
    </div>
  );
}

// ── Estrellas ───────────────────────────────────────────────────────────────
function StarScore({ score, votes, size = "full" }) {
  const total = (score != null && score !== 0) ? score : votes;
  const starSize = size === "mini" ? "clamp(0.8rem, 1.8vw, 1.2rem)"
                 : size === "compact" ? "clamp(1rem, 2.5vw, 1.8rem)"
                 : "clamp(1rem, 2.5vw, 1.4rem)";
  const numSize  = size === "mini" ? "clamp(0.65rem, 1.5vw, 0.95rem)"
                 : size === "compact" ? "clamp(0.85rem, 2vw, 1.4rem)"
                 : "clamp(1rem, 2.5vw, 1.6rem)";
  return (
    <div className="flex items-center justify-center gap-1.5 mt-1">
      <span style={{ color: "#FFD700", fontSize: starSize }}>⭐</span>
      <span className="font-black text-white" style={{ fontSize: numSize }}>
        {typeof total === "number" ? total.toLocaleString() : total}
      </span>
    </div>
  );
}

// ── Config por lugar ─────────────────────────────────────────────────────────
const PLACE_CFG = {
  1: {
    medal: "🥇", crown: true, label: "¡CAMPEÓN!",
    color: "#FFD700", glowColor: "rgba(255,215,0,0.7)",
    cardBg: "linear-gradient(135deg,#7a5000,#FFD700,#7a5000)",
  },
  2: {
    medal: "🥈", crown: false, label: "2do Lugar",
    color: "#C0C0C0", glowColor: "rgba(192,192,192,0.5)",
    cardBg: "linear-gradient(135deg,#555,#C0C0C0,#555)",
  },
  3: {
    medal: "🥉", crown: false, label: "3er Lugar",
    color: "#CD7F32", glowColor: "rgba(205,127,50,0.5)",
    cardBg: "linear-gradient(135deg,#5a2d00,#CD7F32,#5a2d00)",
  },
  4: {
    medal: "4°", crown: false, label: "4to Lugar",
    color: "#60A5FA", glowColor: "rgba(96,165,250,0.4)",
    cardBg: "linear-gradient(135deg,#0f2050,#60A5FA,#0f2050)",
  },
  5: {
    medal: "5°", crown: false, label: "5to Lugar",
    color: "#34D399", glowColor: "rgba(52,211,153,0.4)",
    cardBg: "linear-gradient(135deg,#063322,#34D399,#063322)",
  },
  6: {
    medal: "6°", crown: false, label: "6to Lugar",
    color: "#C084FC", glowColor: "rgba(192,132,252,0.4)",
    cardBg: "linear-gradient(135deg,#2d0b4a,#C084FC,#2d0b4a)",
  },
};

// ── Tarjeta de lugar ─────────────────────────────────────────────────────────
// size: "full" (reveal individual) | "compact" (podio top3) | "mini" (finalistas)
function PlaceCard({ result, place, hasPoints, size = "full" }) {
  const cfg = PLACE_CFG[place] || PLACE_CFG[3];
  const img = imgUrl(
    result.product?.images?.[0]?.imageBase64 ||
    result.product?.images?.[0]?.url ||
    result.product?.images?.[0]
  );

  const imgH = size === "mini"    ? "clamp(60px, 10vh, 150px)"
             : size === "compact" ? "clamp(100px, 20vh, 320px)"
             :                      "clamp(200px, 42vh, 600px)";

  const crownSize = size === "mini"    ? "clamp(1.4rem, 3vw, 2.5rem)"
                  : size === "compact" ? "clamp(2rem, 5vw, 4rem)"
                  :                      "clamp(2.5rem, 7vw, 5.5rem)";

  const medalSize = size === "mini"    ? "clamp(1.2rem, 3vw, 2.2rem)"
                  : size === "compact" ? "clamp(2rem, 5vw, 4rem)"
                  :                      "clamp(2rem, 5.5vw, 4rem)";

  const titleSize = size === "mini"    ? "clamp(0.6rem, 1.4vw, 1rem)"
                  : size === "compact" ? "clamp(0.75rem, 2vw, 1.6rem)"
                  :                      "clamp(0.9rem, 2.5vw, 1.7rem)";

  const coSize    = size === "mini"    ? "clamp(0.5rem, 1.2vw, 0.75rem)"
                  : size === "compact" ? "clamp(0.6rem, 1.5vw, 1.1rem)"
                  :                      "clamp(0.7rem, 1.8vw, 1rem)";

  const labelSize = size === "mini"    ? "clamp(0.65rem, 1.6vw, 1.1rem)"
                  : size === "compact" ? "clamp(0.9rem, 2.2vw, 1.8rem)"
                  :                      "clamp(0.9rem, 2.5vw, 1.6rem)";

  return (
    <motion.div
      initial={{ scale: 0.4, opacity: 0, y: 80 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 180, damping: 16 }}
      className="flex flex-col items-center w-full"
    >
      {cfg.crown && (
        <motion.div
          initial={{ y: -40, opacity: 0, scale: 0 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
          style={{ fontSize: crownSize }}
          className="mb-1"
        >👑</motion.div>
      )}

      <motion.div
        className="w-full rounded-2xl p-px overflow-hidden"
        style={{ background: cfg.cardBg }}
        animate={place === 1 ? {
          boxShadow: [`0 0 30px ${cfg.glowColor}`, `0 0 90px ${cfg.glowColor}`, `0 0 30px ${cfg.glowColor}`],
        } : { boxShadow: `0 0 20px ${cfg.glowColor}` }}
        transition={{ duration: 2.2, repeat: place === 1 ? Infinity : 0 }}
      >
        <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)" }}>
          <div style={{ height: imgH, background: "rgba(0,0,0,0.3)" }}
            className="w-full overflow-hidden relative flex items-center justify-center">
            {img ? (
              <img src={img} alt={result.product?.title} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
                <span style={{ fontSize: size === "mini" ? "2rem" : size === "compact" ? "clamp(2.5rem, 6vw, 5rem)" : "clamp(4rem, 10vw, 8rem)" }}>🍔</span>
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 h-1/3"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)" }} />
          </div>

          <div className={`px-2 ${size === "mini" ? "py-1.5" : "py-2"} text-center`}>
            <div style={{ fontSize: medalSize }}>{cfg.medal}</div>
            <p className="font-black text-white leading-tight mt-0.5 drop-shadow-lg"
              style={{ fontSize: titleSize }}>
              {result.product?.title}
            </p>
            {result.product?.companyName && (
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: coSize }}
                className="font-medium mt-0.5">
                {result.product.companyName}
              </p>
            )}
            <StarScore score={result.score} votes={result.votes} size={size} />
          </div>
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="font-black mt-1.5 text-center"
        style={{ color: cfg.color, fontSize: labelSize }}
      >
        {cfg.label}
      </motion.p>
    </motion.div>
  );
}

// ── Pantalla de anuncio ─────────────────────────────────────────────────────
const ANNOUNCE_CFG = {
  1: { pre: "¡El momento que todos esperaban!", emoji: "🏆", color: "#FFD700" },
  2: { pre: "¡El siguiente puesto es para…!", emoji: "🥈", color: "#C0C0C0" },
  3: { pre: "Prepárense…", emoji: "🥉", color: "#CD7F32" },
  4: { pre: "¡Y en el cuarto lugar…!", emoji: "4️⃣", color: "#60A5FA" },
  5: { pre: "¡Y en el quinto lugar…!", emoji: "5️⃣", color: "#34D399" },
  6: { pre: "Comenzamos con el sexto lugar…", emoji: "6️⃣", color: "#C084FC" },
};

function AnnouncementScreen({ place }) {
  const cfg = ANNOUNCE_CFG[place] || ANNOUNCE_CFG[1];
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-4 text-center">
      <motion.p
        initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }}
        className="font-semibold text-white/80"
        style={{ fontSize: "clamp(1.1rem, 3.5vw, 2.8rem)" }}
      >{cfg.pre}</motion.p>

      <motion.div
        initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 280, delay: 0.2 }}
        style={{ fontSize: "clamp(6rem, 22vw, 14rem)" }}
      >{cfg.emoji}</motion.div>

      <motion.div
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1, repeat: Infinity }}
        style={{ color: cfg.color, fontSize: "clamp(1.4rem, 5vw, 3.5rem)", fontWeight: 900 }}
      >···</motion.div>
    </div>
  );
}

// ── Conteo regresivo ────────────────────────────────────────────────────────
const COUNTDOWN_CFG = {
  1: { color: "#FFD700", shadow: "rgba(255,215,0,0.7)",   label: "¡Y EL GANADOR es..." },
  2: { color: "#C0C0C0", shadow: "rgba(192,192,192,0.5)", label: "¡El 2do lugar es..." },
  3: { color: "#CD7F32", shadow: "rgba(205,127,50,0.5)",  label: "¡El 3er lugar es..." },
  4: { color: "#60A5FA", shadow: "rgba(96,165,250,0.6)",  label: "¡El 4to lugar es..." },
  5: { color: "#34D399", shadow: "rgba(52,211,153,0.6)",  label: "¡El 5to lugar es..." },
  6: { color: "#C084FC", shadow: "rgba(192,132,252,0.6)", label: "¡El 6to lugar es..." },
};

function CountdownScreen({ place }) {
  const [count, setCount] = useState(10);
  const pc = COUNTDOWN_CFG[place] || COUNTDOWN_CFG[1];
  const isLow = count <= 3;
  const numColor  = isLow ? "#FF3B30" : pc.color;
  const numShadow = isLow ? "rgba(255,60,48,0.8)" : pc.shadow;

  useEffect(() => {
    if (count <= 0) return;
    const t = setTimeout(() => setCount(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count]);

  return (
    <div className="flex flex-col items-center justify-center gap-5 px-4 text-center">
      <motion.p
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="font-black uppercase tracking-widest"
        style={{ color: pc.color, fontSize: "clamp(1rem, 3vw, 2.5rem)" }}
      >{pc.label}</motion.p>

      <AnimatePresence mode="wait">
        <motion.div
          key={count}
          initial={{ scale: 1.9, opacity: 0, filter: "blur(12px)" }}
          animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
          exit={{ scale: 0.3, opacity: 0, filter: "blur(8px)" }}
          transition={{ duration: 0.38, ease: [0.34, 1.56, 0.64, 1] }}
          style={{
            fontSize: "clamp(10rem, 40vw, 30rem)",
            fontWeight: 900, lineHeight: 1,
            color: numColor,
            textShadow: `0 0 80px ${numShadow}, 0 0 180px ${numShadow}`,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count === 0 ? "🔥" : count}
        </motion.div>
      </AnimatePresence>

      <div className="flex gap-2 items-center">
        {Array.from({ length: 10 }, (_, i) => {
          const done = i < (10 - count);
          const active = i === (10 - count);
          return (
            <motion.div key={i}
              animate={{ scale: active ? [1, 1.4, 1] : 1 }}
              transition={{ duration: 0.4 }}
              style={{
                width: active ? "clamp(12px, 2.5vw, 20px)" : "clamp(8px, 1.6vw, 13px)",
                height: "clamp(8px, 1.6vw, 13px)",
                borderRadius: "50%",
                backgroundColor: done ? "rgba(255,255,255,0.15)" : numColor,
                opacity: done ? 0.3 : 1,
                transition: "all 0.3s",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Pantalla intro ──────────────────────────────────────────────────────────
function IntroScreen({ event }) {
  const logoSrc = imgUrl(
    event?.eventLogoBase64 || event?.bannerBase64 ||
    event?.image || event?.bannerImage || event?.coverImage ||
    event?.photo || event?.logo || event?.thumbnail
  );

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-4 px-4 text-center w-full">
      {logoSrc ? (
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 130, damping: 14, delay: 0.1 }}
          className="relative"
          style={{ width: "clamp(240px, 55vmin, 680px)", height: "clamp(240px, 55vmin, 680px)" }}
        >
          <img src={logoSrc} alt="logo evento" className="w-full h-full object-contain"
            style={{
              filter: "drop-shadow(0 0 60px rgba(255,215,0,0.6)) drop-shadow(0 0 120px rgba(255,215,0,0.3))",
              borderRadius: "24px",
            }}
          />
          <motion.div className="absolute inset-0 rounded-3xl"
            animate={{ boxShadow: ["0 0 0 0px rgba(255,215,0,0.4)", "0 0 0 24px rgba(255,215,0,0)"] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      ) : (
        <motion.div
          initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
          style={{ fontSize: "clamp(10rem, 32vw, 20rem)", filter: "drop-shadow(0 0 60px rgba(255,215,0,0.7))", lineHeight: 1 }}
        >🏆</motion.div>
      )}

      <motion.p
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
        className="text-white/50" style={{ fontSize: "clamp(0.85rem, 2.2vw, 1.4rem)" }}
      >
        Presiona ▶ para comenzar la presentación
      </motion.p>
    </div>
  );
}

// ── Podio final ─────────────────────────────────────────────────────────────
const PODIUM_GRAD = {
  1: "linear-gradient(to bottom, #FFD700 0%, #A67C00 100%)",
  2: "linear-gradient(to bottom, #D0D0D0 0%, #707080 100%)",
  3: "linear-gradient(to bottom, #CD7F32 0%, #7A4500 100%)",
};
const PODIUM_ORDER = [2, 1, 3];
const CARD_DELAY  = { 1: 0.1, 2: 0.7, 3: 1.2 };
const BLOCK_DELAY = { 1: 1.0, 2: 1.4, 3: 1.8 };

function FinalPodium({ topN, hasPoints }) {
  const byPlace = {};
  topN.forEach((r, i) => { byPlace[i + 1] = r; });
  const podiumH = usePodiumHeights();
  const hasRunners = topN.length > 3;

  return (
    <div className="w-full flex flex-col items-center"
      style={{ maxWidth: "min(1400px, 98vw)", gap: "clamp(8px, 2vh, 24px)" }}>

      {/* ── Top 3: cards + plataformas ── */}
      <div className="w-full flex flex-col items-center">
        <div className="flex items-end justify-center w-full" style={{ gap: "clamp(6px, 1.5vw, 20px)" }}>
          {PODIUM_ORDER.map(place => {
            const result = byPlace[place];
            if (!result) return (
              <div key={place} style={{ flex: place === 1 ? "0 0 36%" : "0 0 28%", maxWidth: place === 1 ? "520px" : "400px" }} />
            );
            return (
              <motion.div key={place}
                initial={{ opacity: 0, y: -80 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: CARD_DELAY[place], type: "spring", stiffness: 180, damping: 18 }}
                style={{ flex: place === 1 ? "0 0 36%" : "0 0 28%", maxWidth: place === 1 ? "520px" : "400px" }}
              >
                <PlaceCard result={result} place={place} hasPoints={hasPoints} size="compact" />
              </motion.div>
            );
          })}
        </div>

        <div className="flex items-end justify-center w-full" style={{ gap: "clamp(6px, 1.5vw, 20px)", marginTop: "10px" }}>
          {PODIUM_ORDER.map(place => (
            <div key={place} style={{ flex: place === 1 ? "0 0 36%" : "0 0 28%", maxWidth: place === 1 ? "520px" : "400px" }}>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: podiumH[place], opacity: 1 }}
                transition={{ delay: BLOCK_DELAY[place], duration: 0.8, type: "spring", stiffness: 160 }}
                style={{
                  background: PODIUM_GRAD[place],
                  borderRadius: "10px 10px 0 0",
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  paddingTop: "clamp(8px, 1.5vh, 18px)", overflow: "hidden",
                }}
              >
                <span style={{ fontSize: "clamp(1.6rem, 4vw, 3rem)", fontWeight: 900, color: "rgba(0,0,0,0.45)", lineHeight: 1 }}>
                  {place}
                </span>
              </motion.div>
            </div>
          ))}
        </div>

        <motion.div
          initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
          transition={{ delay: 2.1, duration: 0.6, ease: "easeOut" }}
          style={{
            width: "100%", height: "clamp(8px, 1.2vh, 14px)",
            background: "linear-gradient(to right, #222, #555, #888, #555, #222)",
            borderRadius: "0 0 8px 8px", transformOrigin: "center",
          }}
        />
      </div>

      {/* ── Finalistas 4-6 ── */}
      {hasRunners && (
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.5 }}
          className="w-full"
        >
          <p className="text-center font-bold mb-2"
            style={{ color: "rgba(255,255,255,0.4)", fontSize: "clamp(0.65rem, 1.6vw, 1rem)", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Finalistas
          </p>
          <div className="flex justify-center" style={{ gap: "clamp(6px, 1.5vw, 16px)" }}>
            {[4, 5, 6].map(place => {
              const r = byPlace[place];
              if (!r) return null;
              return (
                <div key={place} style={{ flex: "0 0 30%", maxWidth: "280px" }}>
                  <PlaceCard result={r} place={place} hasPoints={hasPoints} size="mini" />
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ── Stage durations ──────────────────────────────────────────────────────────
const STAGE_DURATION = {
  intro: 4000,
  announce_6: 2500, countdown_6: 11500, reveal_6: 4000,
  announce_5: 2500, countdown_5: 11500, reveal_5: 4000,
  announce_4: 2500, countdown_4: 11500, reveal_4: 4000,
  announce_3: 3000, countdown_3: 11500, reveal_3: 5000,
  announce_2: 3000, countdown_2: 11500, reveal_2: 5000,
  announce_1: 3500, countdown_1: 11500, reveal_1: 6500,
  podium: 0,
};

// Duracion confetti por reveal
const CONFETTI_DURATION = {
  reveal_1: 5500, reveal_2: 3500, reveal_3: 3000,
  reveal_4: 2000, reveal_5: 2000, reveal_6: 1500,
  podium: 5500,
};

// Cantidad confetti por reveal
const CONFETTI_COUNT = {
  reveal_1: 140, podium: 140,
  reveal_2: 90,  reveal_3: 80,
  reveal_4: 55,  reveal_5: 55, reveal_6: 45,
};

// ── Componente principal ─────────────────────────────────────────────────────
export default function EventCeremony() {
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
  const isAdmin = currentUser?.role === "admin";

  const [event,     setEvent]    = useState(null);
  const [results,   setResults]  = useState(null);
  const [loading,   setLoading]  = useState(true);
  const [noAccess,  setNoAccess] = useState(false);

  const [stageIdx,     setStageIdx]     = useState(0);
  const [playing,      setPlaying]      = useState(false);
  const [fullscreen,   setFullscreen]   = useState(false);
  const [confetti,     setConfetti]     = useState(false);
  const [confettiCnt,  setConfettiCnt]  = useState(80);
  const [showControls, setShowControls] = useState(true);

  // Top N resultados (hasta 6)
  const topN = useMemo(() => results?.results?.slice(0, 6) ?? [], [results]);
  const n    = Math.min(topN.length, 6);

  // Stages dinámicos según cuántos resultados hay
  const stages = useMemo(() => {
    const list = ["intro"];
    for (let p = n; p >= 1; p--) {
      list.push(`announce_${p}`, `countdown_${p}`, `reveal_${p}`);
    }
    list.push("podium");
    return list;
  }, [n]);

  const hasPoints = Array.isArray(results?.votingPoints) && results.votingPoints.length > 1;
  const stage     = stages[stageIdx] ?? "intro";

  // Mantener stageIdx válido si stages cambia
  useEffect(() => {
    setStageIdx(i => Math.min(i, stages.length - 1));
  }, [stages]);

  // Escuchar salida de pantalla completa (tecla Escape)
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Cargar datos
  useEffect(() => {
    Promise.all([API.get(`/events/${id}`), API.get(`/votes/${id}/results`)])
      .then(([evRes, resRes]) => {
        setEvent(evRes.data?.data?.event ?? null);
        setResults(resRes.data?.data ?? null);
      })
      .catch(err => {
        if (err.response?.status === 403) setNoAccess(true);
        else toast.error("Error cargando resultados");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-avance
  useEffect(() => {
    if (!playing) return;
    const dur = STAGE_DURATION[stage];
    if (!dur) { setPlaying(false); return; }
    const t = setTimeout(() => setStageIdx(i => Math.min(i + 1, stages.length - 1)), dur);
    return () => clearTimeout(t);
  }, [playing, stage, stages.length]);

  // Confetti en revelaciones
  useEffect(() => {
    if (stage in CONFETTI_DURATION) {
      setConfettiCnt(CONFETTI_COUNT[stage] ?? 60);
      setConfetti(true);
      const t = setTimeout(() => setConfetti(false), CONFETTI_DURATION[stage]);
      return () => clearTimeout(t);
    }
  }, [stage]);

  // Ocultar controles en pantalla completa
  useEffect(() => {
    if (!fullscreen) { setShowControls(true); return; }
    const t = setTimeout(() => setShowControls(false), 3500);
    return () => clearTimeout(t);
  }, [fullscreen, stageIdx]);

  const handleNext = () => setStageIdx(i => Math.min(i + 1, stages.length - 1));
  const handlePrev = () => setStageIdx(i => Math.max(i - 1, 0));

  const handleShare = async () => {
    const result = await shareContent({ title: "Qkiere", url: window.location.href }).catch(() => "copied_fallback");
    if (result === "copied_fallback") {
      navigator.clipboard?.writeText(window.location.href);
    }
    toast.success(result === "shared" ? "Compartido" : "Enlace copiado");
  };

  // Fullscreen sobre el contenedor mismo — cubre toda la pantalla del proyector
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // Color de fondo según lugar actual
  const bgColor = useMemo(() => {
    if (["reveal_1","announce_1","countdown_1"].includes(stage))
      return "radial-gradient(ellipse at 50% 40%, #3d2200 0%, #1a0a00 50%, #000 100%)";
    if (["reveal_2","announce_2","countdown_2"].includes(stage))
      return "radial-gradient(ellipse at 50% 40%, #0d1a3d 0%, #070c20 50%, #000 100%)";
    if (["reveal_3","announce_3","countdown_3"].includes(stage))
      return "radial-gradient(ellipse at 50% 40%, #2d1000 0%, #160800 50%, #000 100%)";
    if (["reveal_4","announce_4","countdown_4"].includes(stage))
      return "radial-gradient(ellipse at 50% 40%, #0d1535 0%, #070c1c 50%, #000 100%)";
    if (["reveal_5","announce_5","countdown_5"].includes(stage))
      return "radial-gradient(ellipse at 50% 40%, #0a3020 0%, #051810 50%, #000 100%)";
    if (["reveal_6","announce_6","countdown_6"].includes(stage))
      return "radial-gradient(ellipse at 50% 40%, #250838 0%, #12041c 50%, #000 100%)";
    if (stage === "podium")
      return "radial-gradient(ellipse at 50% 40%, #1a0a3d 0%, #080520 55%, #000 100%)";
    return "radial-gradient(ellipse at 50% 40%, #0f0f22 0%, #050510 60%, #000 100%)";
  }, [stage]);

  // Ancho de tarjeta en reveals individuales
  const revealWidth = (place) =>
    place === 1 ? "clamp(320px, 58vw, 760px)"
    : place <= 3 ? "clamp(300px, 52vw, 680px)"
    :               "clamp(260px, 45vw, 560px)";

  const renderStage = () => {
    const placeMatch = stage.match(/^(?:reveal|announce|countdown)_(\d+)$/);
    const place = placeMatch ? parseInt(placeMatch[1]) : null;
    const type  = placeMatch ? stage.split("_")[0] : null;

    if (stage === "intro")  return <IntroScreen event={event} />;
    if (stage === "podium") return <FinalPodium topN={topN} hasPoints={hasPoints} />;

    if (type === "announce") return <AnnouncementScreen place={place} />;
    if (type === "countdown") return <CountdownScreen place={place} />;
    if (type === "reveal") {
      const result = topN[place - 1];
      if (!result) return null;
      return (
        <div style={{ width: revealWidth(place) }}>
          <PlaceCard result={result} place={place} hasPoints={hasPoints} size="full" />
        </div>
      );
    }
    return null;
  };

  // ── Estados de carga / error ──────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white text-xl animate-pulse flex items-center gap-3">
        <Trophy className="animate-spin" size={30} /> Cargando ceremonia…
      </div>
    </div>
  );

  if (noAccess) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 text-center px-4">
      <span className="text-6xl">🔒</span>
      <h2 className="text-white text-2xl font-bold">Resultados no disponibles</h2>
      <p className="text-white/60">El administrador aún no ha publicado los resultados.</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-yellow-400 flex items-center gap-2 font-semibold">
        <ArrowLeft size={18} /> Volver
      </button>
    </div>
  );

  if (!results?.results?.length) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 text-center px-4">
      <span className="text-6xl">📊</span>
      <h2 className="text-white text-2xl font-bold">Sin resultados aún</h2>
      <p className="text-white/60">No hay votos registrados en este evento.</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-yellow-400 flex items-center gap-2 font-semibold">
        <ArrowLeft size={18} /> Volver
      </button>
    </div>
  );

  // ── Render principal ────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="fixed inset-0 overflow-hidden flex flex-col"
      style={{ background: bgColor, transition: "background 1.5s ease" }}
      onMouseMove={() => fullscreen && setShowControls(true)}
    >
      <FloatingParticles />
      <Confetti active={confetti} count={confettiCnt} />

      {/* Contenido central */}
      <div className="relative z-10 flex-1 flex items-center justify-center overflow-hidden min-h-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, scale: 0.88, filter: "blur(12px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="w-full flex items-center justify-center px-3 py-2"
            style={{ maxHeight: "100%", overflowY: "auto" }}
          >
            {renderStage()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Barra de controles */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            className="relative z-20 border-t border-white/10"
            style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(16px)" }}
          >
            <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
              <button onClick={() => navigate(-1)}
                className="text-white/60 hover:text-white transition p-2 rounded-lg hover:bg-white/10">
                <ArrowLeft size={18} />
              </button>

              <div className="flex items-center gap-2 mx-auto">
                <button onClick={handlePrev} disabled={stageIdx === 0}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition disabled:opacity-30">
                  <ChevronLeft size={20} />
                </button>

                <button
                  onClick={() => setPlaying(v => !v)}
                  disabled={stage === "podium"}
                  className="px-5 py-2 rounded-xl font-bold transition flex items-center gap-2 disabled:opacity-40"
                  style={{ background: "#FFD700", color: "#000" }}
                >
                  {playing ? <Pause size={18} /> : <Play size={18} />}
                  {playing ? "Pausa" : stage === "intro" ? "Iniciar" : "Continuar"}
                </button>

                <button onClick={handleNext} disabled={stageIdx === stages.length - 1}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition disabled:opacity-30">
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Indicadores de stage */}
              <div className="hidden sm:flex items-center gap-1">
                {stages.map((_, i) => (
                  <button key={i} onClick={() => setStageIdx(i)}
                    className={`h-2 rounded-full transition-all ${i === stageIdx ? "w-4 bg-yellow-400" : "w-2 bg-white/30 hover:bg-white/50"}`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-1 ml-auto">
                <button onClick={handleShare}
                  className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition">
                  <Share2 size={17} />
                </button>
                <button onClick={toggleFullscreen}
                  className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition">
                  {fullscreen ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
                </button>
              </div>
            </div>

            <div className="flex gap-0.5 px-4 pb-2">
              {stages.map((_, i) => (
                <div key={i} className={`h-0.5 flex-1 rounded-full transition-all ${
                  i < stageIdx ? "bg-yellow-400" : i === stageIdx ? "bg-yellow-400/50" : "bg-white/15"
                }`} />
              ))}
            </div>

            {isAdmin && (
              <div className="px-4 pb-2 flex items-center gap-2 text-xs text-white/40">
                <Star size={10} />
                {results?.publishResults
                  ? "Resultados públicos — cualquier usuario puede ver esta presentación"
                  : "Resultados privados — publícalos en EventManager para compartirlos."
                }
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
